import React, { useState } from 'react';
import { Box, TextField, MenuItem, Button } from '@mui/material';
import { FilterList, Clear } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { WorkflowFilters } from '../../../contexts/WorkflowContext';
import { secondaryButtonStyle } from '../../../constants/ButtonStyles';
import { useWorkflowTypes } from '../hooks/useWorkflowTypes';

interface FilterValues {
  accountId: string;
  assignedTo: string;
  type: string;
}

interface FiltersProps {
  setFilters: (filters: WorkflowFilters) => void;
  filters: WorkflowFilters;
}

export const Filters: React.FC<FiltersProps> = ({ setFilters, filters }) => {
  const theme = useTheme();
  const { workflowTypes } = useWorkflowTypes();
  const [filterValues, setFilterValues] = useState<FilterValues>({
    accountId: filters.accountId || '',
    assignedTo: filters.assignedTo || '',
    type: filters.type || '',
  });

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const handleFilterChange = (field: keyof FilterValues, value: string) => {
    const newFilters = { ...filterValues, [field]: value };
    setFilterValues(newFilters);
    setFilters({
      accountId: newFilters.accountId || null,
      assignedTo: newFilters.assignedTo || null,
      type: newFilters.type || null,
    });
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      accountId: '',
      assignedTo: '',
      type: '',
    };
    setFilterValues(clearedFilters);
    setFilters({
      accountId: null,
      assignedTo: null,
      type: null,
    });
  };

  const hasActiveFilters = Object.values(filterValues).some(
    (value) => value !== ''
  );

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[1],
        flexShrink: 0,
        mb: theme.spacing(2),
        p: theme.spacing(2),
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: theme.spacing(2),
          mb: theme.spacing(1.5),
        }}
      >
        <FilterList sx={{ color: theme.palette.text.secondary }} />
        <Box sx={{ fontWeight: theme.typography.fontWeightBold }}>Filters</Box>
        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<Clear />}
            onClick={handleClearFilters}
            variant="outlined"
            disabled={!hasActiveFilters}
            sx={{
              ...secondaryButtonStyle,
              ml: 'auto',
              paddingInline: 2,
              textTransform: 'none',
            }}
          >
            Clear All
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: theme.spacing(2),
          gridTemplateColumns: {
            md: '1fr 1fr 1fr',
            sm: '1fr 1fr',
            xs: '1fr',
          },
        }}
      >
        <TextField
          label="Servicer Account ID"
          variant="outlined"
          size="small"
          value={filterValues.accountId}
          onChange={(e) => handleFilterChange('accountId', e.target.value)}
          placeholder="Filter by servicer account ID"
          sx={textFieldStyles}
        />

        <TextField
          label="Assigned To"
          variant="outlined"
          size="small"
          value={filterValues.assignedTo}
          onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
          placeholder="Filter by assigned user email or name"
          sx={textFieldStyles}
        />

        <TextField
          select
          label="Type"
          variant="outlined"
          size="small"
          value={filterValues.type}
          sx={{
            ...textFieldStyles,
            '& .MuiSelect-select': {
              textAlign: 'left',
            },
          }}
          onChange={(e) => handleFilterChange('type', e.target.value)}
        >
          {workflowTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
  );
};
